import React from 'react';
import { Calendar, Store, ChevronRight, Clock } from 'lucide-react';
import { useCartAssistant, useCartAssistantInput } from "../../../lib/cartAssistant/CartAssistantProvider";
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../lib/store';
import { CURRENCIES } from '../../../../constants';

interface HistorySummary {
  id: string;
  date: string;
  totalCost: number;
  storeName: string;
  itemCount: number;
}

interface HistoryListCardProps {
  trips: HistorySummary[];
  currencySymbol?: string;
}

export const HistoryListCard: React.FC<{ props: HistoryListCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t, i18n } = useTranslation();
  const { userProfile, currency } = useStore();
  // AI might pass { trips: [...] } or just the array [...] as props
  const rawTrips = (props as any)?.trips || (Array.isArray(props) ? props : []);
  const trips = Array.isArray(rawTrips) ? rawTrips : [];
  const propCurrencySymbol = (props as any)?.currencySymbol;

  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find(c => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || propCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, propCurrencySymbol]);
  
  const assistantInput = useCartAssistantInput();
  const { thread, cancelRun, isIdle } = useCartAssistant();
  const [pendingText, setPendingText] = React.useState<string | null>(null);

  // RESILIENCE: Check if thread is stuck waiting for tool calls
  const isStuck = React.useMemo(() => {
    const threadAny = thread as any;
    return threadAny?.thread?.status === 'requires_action' || 
           (threadAny?.pendingToolCallIds && threadAny.pendingToolCallIds.length > 0) ||
           (!isIdle && !threadAny?.streaming?.isActive);
  }, [thread, isIdle]);

  React.useEffect(() => {
    const fireSubmit = async (isRetry = false) => {
      if (pendingText && assistantInput.value === pendingText && !assistantInput.isDisabled) {
        try {
          // PROACTIVE RECOVERY: If the thread is stuck waiting for tool calls from a previous run,
          // submit() will throw 400. We cancel the existing run here to ensure success.
          if (isStuck) {
            console.warn("[CartAssistant] [HistoryListCard] Thread state is stuck. Attempting to cancel run...");
            if (cancelRun) {
              try {
                await cancelRun();
                console.log("[CartAssistant] [HistoryListCard] Stuck run cancelled proactively.");
                await new Promise(resolve => setTimeout(resolve, 1000));
                // Re-sync value after cancel
                assistantInput.setValue(pendingText);
                await new Promise(resolve => setTimeout(resolve, 300));
              } catch (err) {
                console.error("[CartAssistant] [HistoryListCard] Proactive cancel failed:", err);
              }
            }
          }

          console.log("[CartAssistant] [HistoryListCard] API SUBMIT INITIATED", { isRetry });

          await assistantInput.submit();
          setPendingText(null);
        } catch (err: any) {
          const errorMsg = err?.message || String(err);
          const is400 = errorMsg.includes('400') || 
                       errorMsg.includes('MISSING_RESULTS') || 
                       err?.status === 400 ||
                       err?.code === 'MISSING_RESULTS';
          
          console.error(`[CartAssistant] [HistoryListCard] Submission failed (isRetry: ${isRetry}):`, errorMsg);
          
          if (is400 && !isRetry) {
             const isSdkOutOfSync = isIdle;
             
             if (!isSdkOutOfSync) {
               console.warn("[CartAssistant] [HistoryListCard] 400 MISSING_RESULTS detected. Attempting auto-recovery retry...");
               try {
                 if (cancelRun) {
                   await cancelRun();
                   console.log("[CartAssistant] [HistoryListCard] Stuck run cancelled on error catch. Retrying...");
                   await new Promise(resolve => setTimeout(resolve, 1500));
                   // Re-apply the value before retry
                   assistantInput.setValue(pendingText);
                   await new Promise(resolve => setTimeout(resolve, 500));
                   // AUTO-RETRY
                   return await fireSubmit(true);
                 }
               } catch (recoveryErr) {
                 console.error("[CartAssistant] [HistoryListCard] Recovery retry failed:", recoveryErr);
               }
             } else {
               console.warn("[CartAssistant] [HistoryListCard] SDK state desync detected (Idle but backend waiting for tool). Skipping futile retry.");
             }
          }
          setPendingText(null);
        }
      }
    };

    fireSubmit();
  }, [pendingText, assistantInput.value, assistantInput.isDisabled, assistantInput.submit, thread, cancelRun, isStuck]);

  const handleViewDetails = (id: string, store: string) => {
    if (!assistantInput.isDisabled) {
      const text = t('chat.trip_details_intent', { store, id });
      assistantInput.setValue(text);
      setPendingText(text);
    }
  };

  if (trips.length === 0) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('chat.fetching_history')}</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
        <p className="text-xs font-black text-[#1a2b3c] uppercase tracking-widest">{t('history.no_history')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="p-5 bg-[#1a2b3c] flex items-center gap-3">
        <div className="p-2 bg-emerald-500 rounded-xl text-white shadow-lg">
          <Clock size={18} />
        </div>
        <div>
          <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-400 leading-none mb-1">{t('chat.recent_activity')}</h4>
          <p className="text-xs font-bold text-white/70">{t('chat.last_trips_count', { count: trips.length })}</p>
        </div>
      </div>

      <div className="divide-y divide-gray-50">
        {trips.map((trip, idx) => {
          const tripDate = trip?.date ? new Date(trip.date) : new Date();
          const dateStr = isNaN(tripDate.getTime()) ? t('common.recent') : tripDate.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
          const storeName = trip?.storeName || t('common.unknown_store');
          const cost = typeof trip?.totalCost === 'number' ? trip.totalCost : 0;
          const count = typeof trip?.itemCount === 'number' ? trip.itemCount : 0;
          const tripId = trip?.id || `idx-${idx}`;

          return (
            <button 
              key={tripId}
              onClick={() => handleViewDetails(tripId, storeName)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all group text-left"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0 group-hover:scale-110 transition-transform">
                  <Store size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-black text-[#1a2b3c] truncate">{storeName}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    {dateStr} • {t('history.items_count', { count })}
                  </p>
                </div>
              </div>
              <div className="text-right flex items-center gap-3 shrink-0">
                <span className="text-sm font-black text-emerald-600">{activeCurrencySymbol}{cost.toFixed(2)}</span>
                <ChevronRight size={16} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
